import { Badge } from '@/components/ui/badge'
import { Typography } from '@/components/ui/typography'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
} from '@/components/ui/sheet'
import { PortalInvestmentSection } from '@/components/custom/portal/portal-investment-section'
import { PortalContactsSection } from '@/components/custom/portal/portal-contacts-section'
import { cn } from '@/lib/utils'
import { formatLocationDisplay } from '@/lib/formatters'
import type { PortalCompany } from '@/services/portal.service'

export const PORTAL_SIDE_PANEL_WIDTH = 'w-[360px]'

function normalizeWebsiteUrl(url: string): string {
  const trimmed = url.trim()
  if (!trimmed) return trimmed
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://'))
    return trimmed
  return `https://${trimmed}`
}

function PortalSidePanelContent({ company }: { company: PortalCompany }) {
  const locationLabel = company.location
    ? formatLocationDisplay(company.location)
    : null
  const website = company.website?.trim()
  const websiteHref = website ? normalizeWebsiteUrl(website) : null

  return (
    <div className="space-y-3">
      {/* Sectors */}
      {company.sectors.length > 0 ? (
        <div className="flex flex-wrap gap-1.5">
          {company.sectors.map((sector) => (
            <Badge key={sector.id} variant="outline">
              {sector.name}
            </Badge>
          ))}
        </div>
      ) : null}

      {/* Company name */}
      <Typography variant="h3" className="mb-2 break-words">
        {company.displayName}
      </Typography>

      {/* Description (read-only) */}
      <Typography
        variant="body-sm"
        className={cn('text-primary/80', !company.description && 'italic')}
      >
        {company.description || 'No description'}
      </Typography>

      {/* Company info rows */}
      <div className="space-y-1.5 pt-2">
        <div className="flex items-center justify-between gap-3">
          <Typography as="span" variant="body-sm" className="text-primary/70">
            Web
          </Typography>
          {websiteHref ? (
            <a
              href={websiteHref}
              target="_blank"
              rel="noreferrer"
              className="max-w-[65%] truncate text-right text-xs font-medium text-primary/80 hover:underline"
            >
              {website}
            </a>
          ) : (
            <Typography as="span" variant="body-sm" className="text-primary/70">
              —
            </Typography>
          )}
        </div>
        <div className="flex items-center justify-between gap-3">
          <Typography as="span" variant="body-sm" className="text-primary/70">
            Location
          </Typography>
          {locationLabel ? (
            <Typography
              as="span"
              variant="body-sm"
              className="max-w-[65%] truncate text-right text-primary/80"
            >
              {locationLabel}
            </Typography>
          ) : (
            <Typography as="span" variant="body-sm" className="text-primary/70">
              —
            </Typography>
          )}
        </div>
        {company.geographicFocus ? (
          <div className="flex items-center justify-between gap-3">
            <Typography as="span" variant="body-sm" className="text-primary/70">
              Geographic Focus
            </Typography>
            <Typography
              as="span"
              variant="body-sm"
              className="max-w-[65%] truncate text-right text-primary/80"
            >
              {company.geographicFocus}
            </Typography>
          </div>
        ) : null}
        {company.employeeCount ? (
          <div className="flex items-center justify-between gap-3">
            <Typography as="span" variant="body-sm" className="text-primary/70">
              Employees
            </Typography>
            <Typography as="span" variant="body-sm" className="text-primary/80">
              {company.employeeCount.toLocaleString()}
            </Typography>
          </div>
        ) : null}
      </div>

      <PortalInvestmentSection />

      <PortalContactsSection />
    </div>
  )
}

export function PortalSidePanel({ company }: { company: PortalCompany }) {
  return (
    <div
      className={cn(
        PORTAL_SIDE_PANEL_WIDTH,
        'h-full shrink-0 border-l border-primary/50 bg-sidebar px-3 py-6'
      )}
    >
      <PortalSidePanelContent company={company} />
    </div>
  )
}

interface PortalSidePanelSheetProps {
  company: PortalCompany
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function PortalSidePanelSheet({
  company,
  open,
  onOpenChange,
}: PortalSidePanelSheetProps) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-[min(92vw,420px)] overflow-y-auto bg-sidebar p-0 sm:max-w-none"
      >
        <SheetTitle className="sr-only">Company details</SheetTitle>
        <SheetDescription className="sr-only">
          Company profile, investment, and contacts
        </SheetDescription>
        <div className="px-3 py-4">
          <PortalSidePanelContent company={company} />
        </div>
      </SheetContent>
    </Sheet>
  )
}
